<template>
  <div class="audio-player">
    <div class="audio-player__controls-overlay d-flex">
      <v-button
        round
        ghost
        class="audio-player__secondary-control rewind-button"
        @click="handleRewind"
      >
        <v-icon icon="replay_10"></v-icon>
      </v-button>

      <div class="mx-1 audio-player__actions" data-cy="audio-play">
        <v-button
          class="audio-player__play-button"
          @click="togglePlayback"
          icon
          round
        >
          <template v-if="isLoading">
            <v-loader white />
          </template>
          <template v-else-if="hasFinished">
            <v-icon icon="replay"></v-icon>
          </template>
          <template v-else-if="isPlaying">
            <v-icon icon="pause"></v-icon>
          </template>
          <template v-else>
            <v-icon icon="play_arrow" />
          </template>
        </v-button>
      </div>

      <v-button
        round
        ghost
        class="audio-player__secondary-control forward-button"
        @click="handleForward"
      >
        <v-icon icon="forward_10"></v-icon>
      </v-button>
    </div>
    <div data-cy="audio-player" class="audio-player__audio">
      <div class="audio-player__progress flex-grow-1">
        <input
          class="audio-player__progress-time"
          type="range"
          min="0"
          max="100"
          step="0.01"
          :style="{ '--value': percentage + '%' }"
          :value="percentage"
          @input="handleSeek"
        /><progress
          :class="[
            'audio-player__progress-buffer',
            { '--is-loading': isLoading }
          ]"
          min="0"
          max="100"
          :value="buffered"
          >% buffered</progress
        >
      </div>

      <div class="audio-player__time">
        {{ formattedTime }}
      </div>
      <v-button
        light
        ghost
        small
        class="audio-player__speed"
        @click="handleSpeedUpdate"
      >
        {{ playbackSpeed }}x
      </v-button>
      <v-button
        icon
        small
        ghost
        light
        class="audio-player__volume"
        @click="handleToggleMute"
      >
        <v-icon :icon="isMuted ? 'volume_off' : 'volume_up'"></v-icon>
      </v-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex"

import dayjs from "dayjs"

import { isBuilderContext, getImageUrl } from "@/lib/utils"

export default {
  props: {
    audio: { type: Object, required: true },
    autoplay: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    thumbnail: { type: String, default: null }
  },

  mounted() {
    this.setupGlobalPlayer()

    // TODO: Consider preloading if not current source
  },

  computed: {
    ...mapState("pages", ["currentPage"]),
    ...mapGetters("media", ["globalPlayer"]),
    ...mapGetters(["appIcon"]),

    percentage() {
      return this.isCurrentAudio ? this.globalPlayer.currentTimeAsPercentage : 0
    },

    buffered() {
      return this.isCurrentAudio ? this.globalPlayer.buffered * 100 : 0
    },

    isCurrentAudio() {
      return this.globalPlayer
        ? this.globalPlayer.isCurrentAudio(this.audio.audio_id)
        : false
    },

    playbackSpeed() {
      return this.isCurrentAudio ? this.globalPlayer.speed : 1
    },

    isMuted() {
      return this.isCurrentAudio ? this.globalPlayer.isMuted : false
    },

    hasFinished() {
      return this.isCurrentAudio ? this.globalPlayer.hasFinished : false
    },

    isPlaying() {
      return this.isCurrentAudio ? this.globalPlayer.isPlaying : false
    },

    isLoading() {
      return this.isCurrentAudio ? this.globalPlayer.isLoading : false
    },

    formattedTime() {
      const audio = this.isCurrentAudio
        ? this.globalPlayer.currentSource.audio
        : this.audio

      let format = audio.duration > 3600 ? "H:mm:ss" : "mm:ss"

      if (this.isCurrentAudio) {
        format = this.globalPlayer.currentTime > 0 ? `-${format}` : format

        return dayjs
          .duration(
            Math.max(
              this.globalPlayer.currentSource.audio.duration -
                this.globalPlayer.currentTime,
              0
            ) * 1000
          )
          .format(format)
      } else {
        return dayjs.duration(this.audio.duration * 1000).format(format)
      }
    }
  },

  methods: {
    ...mapActions("media", ["setupGlobalPlayer"]),

    handleSeek(e) {
      this.ensureCurrentAudio(async didChange => {
        if (didChange) {
          await this.globalPlayer.play()
        }

        this.globalPlayer.seek(e.target.value)
      })
    },

    handleForward() {
      this.ensureCurrentAudio(() => this.globalPlayer.forward(10))
    },

    handleRewind() {
      this.ensureCurrentAudio(() => this.globalPlayer.rewind(10))
    },

    handleSpeedUpdate() {
      if (this.isCurrentAudio) {
        this.globalPlayer.togglePlaybackRate()
      }
    },

    handleToggleMute() {
      this.ensureCurrentAudio(() => this.globalPlayer.toggleMuted())
    },

    togglePlayback(e) {
      e.stopPropagation()
      e.preventDefault()

      if (isBuilderContext()) return

      this.ensureCurrentAudio(didChange => {
        return didChange ? this.globalPlayer.play() : this.globalPlayer.toggle()
      })
    },

    ensureCurrentAudio(callback) {
      if (this.isCurrentAudio) {
        return callback(false)
      }

      if (!this.globalPlayer) {
        this.setupGlobalPlayer()
      }

      this.globalPlayer.setSource({
        audio: {
          id: this.audio.audio_id,
          title: this.audio.title,
          duration: this.audio.duration,
          url: this.audio.embed_link,
          thumbnail: this.thumbnail || getImageUrl(this.appIcon) || null
        },
        loop: this.loop,
        autoplay: this.autoplay,
        page: {
          title: this.currentPage.title,
          path: this.currentPage.path
        }
      })

      callback(true)
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/partials/audio";

$plyr-controls--height: 44px;
$plyr-range-track-height: 5px;

.audio-player {
  z-index: $video--controls-z-index;

  .material-icons-round {
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}

.audio-player__play-button {
  // Hardcoded white & black to prevent --dark-mode overrides
  color: white;
  background: rgba(black, 0.56);
  height: $size--10 !important;
  width: $size--10 !important;
  max-height: $size--10 !important;
  max-width: $size--10 !important;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;

  &:hover {
    cursor: pointer;
  }

  &:active {
    background: rgba(black, 0.76);
  }
}

.audio-player__controls-overlay {
  height: calc(100% - #{$plyr-controls--height});
  align-items: center;
  color: $color-ui--white;
  height: auto;
  justify-content: center;
  user-select: none;
}

.v-button.audio-player__secondary-control {
  color: $color-ui--white;
  height: $size--8;
  width: $size--8;
  font-size: 24px;
  filter: drop-shadow(0 3px 8px rgba(60, 66, 87, 0.8));

  &:hover {
    background: rgba($color-rgb--black, 0.16);
  }
}

.audio-player__audio {
  width: 100%;
  bottom: 0;
  z-index: $video--controls-z-index;

  display: flex;
  align-items: center;
  padding-left: $size--2;
  padding-right: $size--2;
  padding-bottom: $size--1;
}

.audio-player__progress {
  position: relative;
}

.audio-player__time {
  color: $color-fixed--white;
  font-size: 14px;
  font-weight: 500;
  width: 4rem;
  text-align: center;
}

.audio-player__speed,
.audio-player__volume {
  color: $color-fixed--white;
  cursor: pointer;
  user-select: none;
  text-shadow: 0 3px 8px rgba(60, 66, 87, 0.8);
  padding: 0 !important;
  background-color: transparent !important;
  text-overflow: auto;
}

.v-button.audio-player__volume {
  font-size: 18px !important;
  width: 2.25rem;
}

.audio-player__speed {
  font-size: 14px !important;
  font-weight: 500;
  width: 2.4rem;

  &:hover {
    background-color: rgba($color-rgb--black, 0.56);
  }
}

.audio-player__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}
</style>
